import { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Crop, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cropImage } from "@/lib/imageTools";
import { uploadPhoto } from "@/components/PhotoUploader";

export default function CropDialog({ analysisId, open, onOpenChange, onUploaded }) {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(2);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  useEffect(() => { if (!open) { setFile(null); setUrl(null); setZoom(2); } }, [open]);

  const pick = (f) => { if (f) { setFile(f); setUrl(URL.createObjectURL(f)); } };
  const onComplete = useCallback((_, px) => setArea(px), []);

  const send = async () => {
    setBusy(true);
    try {
      await uploadPhoto(analysisId, await cropImage(file, area), "ritaglio");
      onUploaded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[22px] text-[#0D2517]">Ritaglio ravvicinato</DialogTitle>
          <DialogDescription className="text-[17px] leading-relaxed text-[#2D3A30]">
            Scegli la foto originale, ingrandisci e centra il sintomo (insetti, macchie, lesioni). Il ritaglio viene inviato ad alta risoluzione.
          </DialogDescription>
        </DialogHeader>
        <input ref={input} type="file" accept="image/*" hidden onChange={(e) => pick(e.target.files?.[0])} data-testid="crop-file-input" />
        {!url ? (
          <button onClick={() => input.current?.click()} className="btn-primary min-h-[48px] w-full !text-base" data-testid="crop-pick-button"><ImagePlus size={18} /> Scegli la foto</button>
        ) : (
          <>
            <div className="relative h-72 w-full overflow-hidden rounded-xl bg-black" data-testid="crop-area">
              <Cropper image={url} crop={crop} zoom={zoom} aspect={1} maxZoom={8} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
            </div>
            <input type="range" min={1} max={8} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-full accent-[#0D2517]" aria-label="Ingrandimento" data-testid="crop-zoom" />
            <button onClick={send} disabled={busy || !area} className="btn-primary min-h-[48px] w-full !text-base" data-testid="crop-send-button">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Crop size={18} />} Invia il ritaglio
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
